import { useCallback, useEffect, useMemo, useState } from "react";
import { FETCH_STATE } from "constants/fetchState";

/**
 * @typedef {Object} FetchState
 * @property {'idle' | 'loading' | 'success' | 'error'} state
 * @property {Error} error
 * @property {boolean} isLoading
 * @property {boolean} isSuccess
 * @property {boolean} isError
 */

/**
 *
 * @typedef {Object} SetFetchState
 * @property {Function} toLoading
 * @property {Function} toError
 * @property {Function} toSuccess
 */

/**
 *
 * @param {Object} initialState
 * @returns {{fetchState: FetchState, setFetchState: SetFetchState}}
 */
export const useFetchState = (initialState) => {
  const [state, setState] = useState(initialState ?? FETCH_STATE.IDLE);
  const [error, setError] = useState();
  const isLoading = useMemo(() => state === FETCH_STATE.LOADING, [state]);
  const isSuccess = useMemo(() => state === FETCH_STATE.SUCCESS, [state]);
  const isError = useMemo(() => state === FETCH_STATE.ERROR, [state]);

  const setToLoading = useCallback(() => {
    setState(FETCH_STATE.LOADING);
  }, []);
  const setToError = useCallback((error) => {
    setError(error);
    setState(FETCH_STATE.ERROR);
  }, []);
  const setToSuccess = useCallback(() => {
    setState(FETCH_STATE.SUCCESS);
  }, []);

  const fetchState = useMemo(
    () => ({
      state,
      error,
      isLoading,
      isSuccess,
      isError,
    }),
    [state, error, isLoading, isSuccess, isError],
  );

  const setFetchState = useMemo(
    () => ({
      toLoading: setToLoading,
      toError: setToError,
      toSuccess: setToSuccess,
    }),
    [setToLoading, setToError, setToSuccess],
  );

  return { fetchState, setFetchState };
};

/**
 *
 * @param {Object} response
 * @param {SetFetchState} setFetchState
 * @returns {{fetchState: FetchState, trigger: Function}}
 */
export const useFetchStateHelper = (
  { status, error, isPending, isLoading, isError, isSuccess },
  setFetchState,
) => {
  const [allowUpdates, setAllowUpdates] = useState(false);

  const trigger = useCallback(() => {
    setAllowUpdates(true);
  }, []);

  const fetchState = useMemo(
    () => ({
      error,
      isLoading: isPending,
      isError,
      isSuccess,
      state: status,
    }),
    [error, status, isPending, isError, isSuccess],
  );

  useEffect(() => {
    if (allowUpdates) {
      setFetchState?.(fetchState);
    }
  }, [fetchState, allowUpdates, setFetchState]);

  return useMemo(() => ({ fetchState, trigger }), [fetchState, trigger]);
};

export const useFetchStateMerger = (...args) => {
  const { fetchState, setFetchState } = useFetchState();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchStates = useMemo(() => args, [...args.flatMap(Object.values)]);

  useEffect(() => {
    if (!Array.isArray(fetchStates) || fetchStates.length === 0) {
      return;
    }

    const errors = fetchStates.filter((s) => s.isError).map((s) => s.error);

    if (errors.length > 0) {
      setFetchState.toError(errors[0]);
    } else if (fetchStates.every((s) => s.isSuccess)) {
      setFetchState.toSuccess();
    } else if (fetchStates.some((s) => s.isLoading)) {
      setFetchState.toLoading();
      // // } else {
      //   setFetchState.toLoading();
    }
  }, [fetchStates, setFetchState]);

  return { fetchState, setFetchState };
};

export const useMergedFetchState = (fetchStatesToMerge = []) => {
  const { fetchState, setFetchState } = useFetchState();

  const activeFetchStates = fetchStatesToMerge.filter(
    (state) => state.status !== "idle",
  );

  useEffect(() => {
    const isLoading = activeFetchStates.some((state) => state.isLoading);
    if (isLoading) {
      setFetchState.toLoading();
      return;
    }
    const error = activeFetchStates.find((state) => state.isError)?.error;
    if (error) {
      setFetchState.toError(error);
      return;
    }
    const isSuccess = activeFetchStates.every((state) => state.isSuccess);
    if (isSuccess) {
      setFetchState.toSuccess();
    }
  }, [fetchStatesToMerge, activeFetchStates, setFetchState]);

  return { fetchState };
};
